import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Drawer,
  CircularProgress,
  IconButton,
  Slider,
} from "@mui/material";
import HLSPlayer from "./HLSPlayer"; // your HLS player component
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import logo from "../static/logo.png";
import { styled } from "@mui/material/styles";
import KeyMoments from "./KeyMoments";
import {
  AccountCircle,
  PauseCircleFilledOutlined,
  PlayCircleFilledOutlined,
  VolumeMuteOutlined,
  VolumeOffOutlined,
} from "@mui/icons-material";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const KeySegments = React.memo(({ queryText, keySegments, socketComplete }) => {
  const identityPoolId = process.env.REACT_APP_IDENTITY_POOL_ID;

  const [keyMoments, setKeyMoments] = useState([]);
  const [updatedKeySegments, setUpdatedKeySegments] = useState([]);
  const [segmentDetails, setSegmentDetails] = useState();
  const [openKeyMomentsDrawer, setOpenKeyMomentsDrawer] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleCardClick = (segment) => {
    setSegmentDetails(segment);
    setKeyMoments(segment?.key_moments);
    setOpenKeyMomentsDrawer(true);
    setVideoUrl(segment?.url); // you could update this if needed
  };

  const handleCloseDrawer = () => {
    setOpenKeyMomentsDrawer(false);
  };

  const formattedDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  // Function to fetch the HLS manifest for a single video.
  const fetchHLSManifestForVideo = async (segment) => {
    const bucketName = segment?.video_s3_bucket;
    const objectKey = segment?.video_s3_key;
    try {
      const idToken = sessionStorage.getItem("idToken");
      if (!idToken) {
        console.error("No idToken found in sessionStorage.");
        return segment;
      }

      const s3Client = new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: fromCognitoIdentityPool({
          clientConfig: { region: process.env.REACT_APP_AWS_REGION },
          identityPoolId: identityPoolId,
          logins: {
            [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_USER_POOL_ID}`]:
              idToken,
          },
        }),
      });

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: objectKey,
      });
      const data = await s3Client.send(command);
      const manifestText = await new Response(data.Body).text();
      const lines = manifestText.split("\n");

      // Update lines that reference .ts segments with presigned URLs
      const updatedLines = await Promise.all(
        lines.map(async (line) => {
          if (line.trim().endsWith(".ts")) {
            const presignedUrl = await generatePresignedUrlForTs(
              line.trim(),
              s3Client,
              segment
            );
            return presignedUrl;
          }
          return line;
        })
      );

      const updatedManifestText = updatedLines.join("\n");

      const updatedBlob = new Blob([updatedManifestText], {
        type: "application/vnd.apple.mpegurl",
      });
      const updatedManifestUrl = window.URL.createObjectURL(updatedBlob);
      return updatedManifestUrl;
    } catch (error) {
      console.error(
        "Error fetching HLS manifest from S3 for video id",
        segment.id,
        error
      );
      return segment;
    }
  };

  const generatePresignedUrlForTs = async (tsFileKey, s3Client, segment) => {
    let formattedTsFileKey = tsFileKey.replace(/%20/g, " ");

    // let _tsFileKey = segment.video_s3_key
    //   .split("/")
    //   .slice(3, 4)
    //   .concat([formattedTsFileKey])
    //   .join("/");

    let _key = formattedTsFileKey?.split("/")?.slice(3)?.join("/");
    let _bucketName = formattedTsFileKey?.split("/")?.slice(2, 3);

    const command = new GetObjectCommand({
      // Bucket: segment.video_s3_bucket,
      Bucket: _bucketName[0],
      Key: _key,
    });
    const presignedUrl = await getSignedUrl(s3Client, command, {
      expiresIn: 3600,
    });
    return presignedUrl;
  };

  const fetchURLs = async () => {
    if (keySegments?.length > 0) {
      let batch = 5;
      let index = 0;

      while (index < keySegments.length) {
        const segmentBatch = keySegments.slice(index, index + batch);
        for (let i = index; i < batch; i++) {
          let segment = keySegments[i];
          let s3FinalUrl = await fetchHLSManifestForVideo(segment);
          setUpdatedKeySegments((prev) => [
            ...prev,
            {
              ...segment,
              url: s3FinalUrl,
              playing: false,
              mute: false,
              current_start_time: segment?.video_start_time,
              current_end_time: segment?.video_end_time,
              current_time: 0,
            },
          ]);
        }
        index = batch;
        batch += 5;
      }
    } else {
      setErrorMsg("Sorry, No Key Segments Found for the topic");
    }
  };

  const playPauseHandler = (segment, index) => {
    let temp = { ...segment };
    temp.playing = !temp.playing;
    setUpdatedKeySegments((prev) => {
      const updatedSegments = [...prev];
      updatedSegments[index] = { ...temp }; // Replace the segment
      return updatedSegments;
    });
  };

  const muteHandler = (segment, index) => {
    let temp = { ...segment };
    temp.mute = !temp.mute;
    setUpdatedKeySegments((prev) => {
      const updatedSegments = [...prev];
      updatedSegments[index] = { ...temp }; // Replace the segment
      return updatedSegments;
    });
  };

  const formatIntervalLabel = (value) => {
    if (!value) return "00:00";
    const minutes = Math.floor(value / 60); // Get full minutes
    const seconds = Math.floor(value % 60); // Get remaining seconds

    return `${minutes < 10 ? "0" : ""}${minutes}:${
      seconds < 10 ? "0" : ""
    }${seconds}`;
  };

  const handleRangeSlider = (event, newValue, segment, index) => {
    let temp = { ...segment };
    temp.current_start_time = newValue?.[0] * 1000;
    temp.current_end_time = newValue?.[1] * 1000;
    temp.playing = false;
    setUpdatedKeySegments((prev) => {
      const updatedSegments = [...prev];
      updatedSegments[index] = { ...temp }; // Replace the segment
      return updatedSegments;
    });
  };

  const handleUpdateCurrentTime = (index, value, value2) => {
    let temp = { ...updatedKeySegments?.[index] };
    temp.current_time = Math.floor(value2);
    setUpdatedKeySegments((prev) => {
      const updatedSegments = [...prev];
      updatedSegments[index] = { ...temp }; // Replace the segment
      return updatedSegments;
    });
  };

  const handleAutoPause = (index) => {
    let temp = { ...updatedKeySegments?.[index] };
    temp.playing = false;
    temp.current_start_time = temp.video_start_time;
    temp.current_time = 0;
    setUpdatedKeySegments((prev) => {
      const updatedSegments = [...prev];
      updatedSegments[index] = { ...temp }; // Replace the segment
      return updatedSegments;
    });
  };

  useEffect(() => {
    if (socketComplete) fetchURLs();
  }, [socketComplete]);

  return (
    <Grid container display="block">
      {updatedKeySegments?.length < 1 && errorMsg && (
        <Grid item sm={12} md={8}>
          <Typography
            variant="body1"
            style={{ margin: "1rem 0", color: "#666666" }}
          >
            {errorMsg}
          </Typography>
        </Grid>
      )}
      <Grid item sm={12} md={8} mb={"0.5rem"}>
        <Box
          sx={{
            display: "flex",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          <img
            src={logo}
            width={24}
            height={"auto"}
            alt="logo"
            style={{ marginRight: "10px" }}
          />
          {updatedKeySegments?.length > 0 && (
            <code className="progress">Key Segments</code>
          )}
          {updatedKeySegments?.length < 1 && !errorMsg && (
            <code className="progress">
              <span>Generating Key Segments</span>
              <CircularProgress
                size={18}
                thickness={8}
                sx={{
                  marginLeft: "12px",
                  verticalAlign: "middle",
                  color: "#AC1FFF",
                }}
              />
            </code>
          )}
        </Box>
      </Grid>
      {updatedKeySegments?.length > 0 && (
        <Box className="key-segment-wrapper">
          {updatedKeySegments?.map((segment, index) => {
            console.log("segment", segment);
              return (
                <Box style={{padding:"4px"}}>
                  <Card
                    key={index}
                    sx={{ padding: "0 0 0 0", overflow: "auto" }}
                  >
                    <CardContent sx={{ paddingBottom: "16px !important" }}>
                      
                      {/* <CustomReactPlayer
                     width={"400px"}
                     height={"200px"}
                     style={{ marginBottom: "0.5rem" }}
                     url={segment?.url}
                     controls={true}
                     onClick={(e) => e.stopPropagation()}
                   /> */}
                      <Box
                        sx={{
                          margin: "0 auto",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                      >
                        <HLSPlayer
                          streamUrl={segment?.url || ""}
                          controls={false}
                          playing={segment?.playing}
                          mute={segment?.mute}
                          showControls={false}
                          startTime={Math.floor(
                            segment?.current_start_time / 1000
                          )}
                          endTime={Math.floor(segment?.current_end_time / 1000)}
                          currentTime={segment?.current_time}
                          handleUpdateCurrentTime={handleUpdateCurrentTime}
                          handleAutoPause={handleAutoPause}
                          index={index}
                        />

                        {/* <Card sx={{ marginTop: "2px", width: "100%" }}>
                          <Box
                            sx={{
                              padding: "0.2rem 0.3rem",
                              display: "flex",
                              gap: "1rem",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap="0.5rem"
                            >
                              {segment.playing ? (
                                <PauseCircleFilledOutlined
                                  style={{
                                    fontSize: 28,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    playPauseHandler(segment, index)
                                  }
                                />
                              ) : (
                                <PlayCircleFilledOutlined
                                  style={{
                                    fontSize: 28,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    playPauseHandler(segment, index)
                                  }
                                />
                              )}
                              {segment.mute ? (
                                <VolumeOffOutlined
                                  onClick={() => muteHandler(segment, index)}
                                  style={{
                                    cursor: "pointer",
                                    fill: "#AC1FFF",
                                  }}
                                />
                              ) : (
                                <VolumeMuteOutlined
                                  onClick={() => muteHandler(segment, index)}
                                  style={{ cursor: "pointer" }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption">
                              {formatIntervalLabel(
                                Math.floor(segment?.current_time)
                              ) +
                                "/" +
                                formatIntervalLabel(
                                  Math.floor(segment?.video_duration / 1000)
                                )}
                            </Typography>
                          </Box>
                        </Card>
                        <Slider
                          valueLabelDisplay="on"
                          value={[
                            Math.floor(segment?.current_start_time / 1000),
                            Math.floor(segment?.current_end_time / 1000),
                          ]}
                          valueLabelFormat={(v) => formatIntervalLabel(v)}
                          onChange={(e, value) => {
                            handleRangeSlider(e, value, segment, index);
                          }}
                          // onChangeCommitted={handleRangeCommitted}
                          min={Math.floor(segment?.video_start_time / 1000)}
                          max={Math.floor(segment?.video_end_time / 1000)}
                          step={1}
                          sx={{
                            display: "none",
                            width: "90%",
                            padding: 0,
                            marginBottom: 5,
                            marginTop: 1,
                            "& .MuiSlider-thumb": {
                              backgroundColor: "#ffffff",
                              border: "4px solid #666666",
                              height: 12,
                              width: 12,
                            },
                            "& .MuiSlider-rail": {
                              backgroundColor: "#cccccc",
                            },
                            "& .MuiSlider-track": {
                              backgroundColor: "#666666",
                              border: 0,
                            },
                            "& .MuiSlider-mark": {
                              backgroundColor: "#AC1FFF",
                              height: 8,
                            },
                            "& .MuiSlider-valueLabel": {
                              backgroundColor: "#666666",
                              borderRadius: 12,
                              bottom: -54,
                              paddingTop: "2px",
                              paddingBottom: "2px",
                              top: "auto",
                              fontSize: 12,
                              "&::before": {
                                top: -7,
                                bottom: "auto",
                              },
                            },
                          }}
                        /> */}
                      </Box>
                      <Typography
                        variant="body1"
                        textAlign="center"
                        fontFamily={"Roboto Condensed"}
                        className="key-segment-headline"
                        mb={1}
                      >
                        {segment?.headline}
                      </Typography>
                      {/* <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={1}
                        px={1}
                      >
                        <Typography color={"textSecondary"} variant="caption">
                          {formattedDate(
                            segment?.video_timestamp?.epoch_millis
                          )}
                        </Typography>
                        <Button
                          className="key-segments-view-details-btn"
                          size="small"
                          onClick={() => handleCardClick(segment)}
                        >
                          View Details
                        </Button>
                      </Box> */}
                    </CardContent>
                  </Card>
                </Box>
              );
          })}
        </Box>
      )}
      <Drawer
        open={openKeyMomentsDrawer}
        onClose={() => handleCloseDrawer()}
        anchor="right"
        PaperProps={{
          sx: {
            width:'515px',
            backgroundColor:
              sessionStorage.getItem("mode") === "light"
                ? "#FFFFFF"
                : "#000000",
            border: 0,
            // margin: "12px 0",
            borderRadius: "12px",
            // height: "calc(100% - 84px)",
            boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
          },
        }}
        // sx={{
        //   width: window.innerWidth * 0.33,
        //   flexShrink: 0,
        //   "& .MuiDrawer-paper": {
        //     width:
        //       window.innerWidth < 600
        //         ? "calc(100% - 24px)"
        //         : window.innerWidth * 0.33,
        //     boxSizing: "border-box",
        //   },
        // }}
      >
        <DrawerHeader
          sx={{
            position: "fixed",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
            borderRadius: "24px 24px 0 0",
            width: "inherit",
            margin: 0,
            borderBottom: "1px solid #ccc",
            zIndex: 999,
          }}
        >
          <Box display='flex' alignItems='center' ml={2}>
            <VideoLibraryOutlinedIcon
              sx={{ mr: 1, fontSize: 24, verticalAlign: "middle" }}
            />
            <Typography>Segment Details</Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer}>{<CloseIcon />}</IconButton>
        </DrawerHeader>
        <KeyMoments
          segmentDetails={segmentDetails}
          keyMoments={keyMoments}
          videoUrl={videoUrl}
        />
      </Drawer>
    </Grid>
  );
});

export default KeySegments;